import {
    Button,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    IconButton,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { Stack } from '@mui/system';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import { Ticket } from 'types/User/firebaseTypes/ticket';
import { format, getDay } from 'date-fns';
import { FC, useEffect, useState } from 'react';
import styles from './DeliveriesWeekView.module.scss';
import {
    convertDeliveryStatusToDisplayName,
    convertDeliveryStatusToInternalName,
    convertDeliveryStatusToStatusColor,
} from 'lib/constants';
import {
    Person,
    SettingsSuggest,
    Verified,
    Warning,
} from '@mui/icons-material';
import classNames from 'classnames';
import { maxValidFill } from 'helpers/planning';
import { isToday } from 'date-fns';
import { LoadingSpinner } from 'components/data/LoadingSpinner';
import { DeviceLookup } from 'views/Planning/Planning';
import { IssueCauseIcon } from 'components/common/IssueCauseIcon/IssueCauseIcon';
import { useNavigate } from 'react-router-dom';

interface WeekListProps {
    tickets: Array<Ticket | null> | null;
    date: Date;
    weekday: string;
    setActiveTicket: (t: Ticket) => void;
    selectedTickets: string[];
    setSelectedTickets: (s: string[]) => void;
    devicesList: DeviceLookup;
    isLoading?: boolean;
}

export const WeekList = ({
    tickets,
    date,
    setActiveTicket,
    setSelectedTickets,
    selectedTickets,
    weekday,
    devicesList,
    isLoading,
}: WeekListProps) => {
    const theme = useTheme();
    const weekAmountDelivered = tickets?.reduce((total, ticket) => {
        if (ticket?.status === 'done')
            return total + Number(ticket?.gallonsDelivered || 0);
        else return 0;
    }, 0);
    const weekAmountPlanned = tickets?.reduce((total, ticket) => {
        if (!ticket) {
            return total;
        }

        const device = devicesList[ticket.deviceId];

        if (!device) {
            return total;
        }

        return (
            total + Number(ticket?.plannedGallons || maxValidFill(device) || 0)
        );
    }, 0);
    const formattedDate = date ? format(date, 'MMM d') : '';
    // adding this to draggableId so we can add ticket to correct batch on week change
    const dayIndex = getDay(date) - 1 >= 0 ? getDay(date) - 1 : 6;

    const [isMobile, setIsMobile] = useState<boolean>(
        window.innerWidth <= 1399
    );

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth <= 1399) {
                setIsMobile(true);
            } else {
                setIsMobile(false);
            }
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [setIsMobile]);

    return (
        <div
            className={styles.weekContainer}
            data-testid={`weeklist-${weekday}`}
        >
            <Card
                className={styles.weekdayHeader}
                sx={{
                    background: isToday(date) ? theme.palette.primary.main : '',
                    filter: isToday(date)
                        ? 'saturate(70%) brightness(120%)'
                        : '',
                }}
            >
                <CardHeader subheader={`${weekday}, ${formattedDate}`} />
                <CardContent className={styles.headerSum}>
                    <div>
                        Gal. Planned:{' '}
                        {weekAmountPlanned
                            ? Math.round(weekAmountPlanned).toLocaleString()
                            : '-'}
                    </div>
                    <div>
                        Gal. Delivered:{' '}
                        {weekAmountDelivered
                            ? Math.round(weekAmountDelivered).toLocaleString()
                            : '-'}
                    </div>
                </CardContent>
            </Card>
            <Droppable
                droppableId={`week-list-${weekday}`}
                mode="virtual"
                renderClone={(provided, snapshot, rubric) => {
                    const ticketToClone = tickets?.find(
                        (ticket) =>
                            ticket?.id === rubric.draggableId?.split('.')?.[0]
                    );

                    return ticketToClone ? (
                        <div
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            ref={provided.innerRef}
                        >
                            <TicketItem
                                ticket={ticketToClone}
                                setActiveTicket={setActiveTicket}
                                selectedTickets={selectedTickets}
                                setSelectedTickets={setSelectedTickets}
                                devicesList={devicesList}
                            />
                        </div>
                    ) : (
                        <div />
                    );
                }}
            >
                {(provided, snapshot) => {
                    return tickets ? (
                        <div
                            ref={provided.innerRef}
                            className={classNames(styles.weekList, {
                                [styles.isDraggingOver]:
                                    snapshot.isDraggingOver,
                            })}
                            {...provided.droppableProps}
                            id={format(date, 'yyyy/MM/dd')}
                        >
                            <Stack>
                                {tickets.map((ticket, index) => {
                                    if (ticket) {
                                        if (
                                            ticket.id ===
                                            snapshot.draggingFromThisWith?.split(
                                                '.'
                                            )?.[0]
                                        ) {
                                            return (
                                                <div
                                                    key={ticket.id}
                                                    className={
                                                        styles.placeholder
                                                    }
                                                />
                                            );
                                        }

                                        return (
                                            <Draggable
                                                key={`${ticket.id}-${index}`}
                                                draggableId={`${ticket.id}.${dayIndex}`}
                                                index={index}
                                                isDragDisabled={
                                                    isMobile || isLoading
                                                }
                                            >
                                                {(provided) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.dragHandleProps}
                                                        {...provided.draggableProps}
                                                    >
                                                        {
                                                            <TicketItem
                                                                ticket={ticket}
                                                                setActiveTicket={
                                                                    setActiveTicket
                                                                }
                                                                selectedTickets={
                                                                    selectedTickets
                                                                }
                                                                setSelectedTickets={
                                                                    setSelectedTickets
                                                                }
                                                                devicesList={
                                                                    devicesList
                                                                }
                                                                isLoading={
                                                                    isLoading
                                                                }
                                                            />
                                                        }
                                                    </div>
                                                )}
                                            </Draggable>
                                        );
                                    }

                                    return (
                                        <div key={`missing-ticket-${index}`} />
                                    );
                                })}
                            </Stack>
                        </div>
                    ) : (
                        // innerRef needs to be provided or pangea will throw errors
                        <div ref={provided.innerRef} />
                    );
                }}
            </Droppable>
        </div>
    );
};

const TicketItem: FC<{
    ticket: Ticket;
    setActiveTicket: (t: Ticket) => void;
    selectedTickets: string[];
    setSelectedTickets: (s: string[]) => void;
    devicesList: DeviceLookup;
    isLoading?: boolean;
}> = ({
    ticket,
    setActiveTicket,
    selectedTickets,
    setSelectedTickets,
    devicesList,
    isLoading,
}) => {
    const device = devicesList[ticket.deviceId];
    const missingInfo =
        convertDeliveryStatusToInternalName[ticket?.status] === 'done' &&
        (!ticket.dateDelivered || !ticket.gallonsDelivered);
    const navigate = useNavigate();
    return (
        <Card
            className={`${styles.ticketCard} ${
                ticket?.createdBy === 'Auto' ? styles.autoDeliveryTicket : ''
            }`}
        >
            {isLoading && (
                <div className={styles.cardLoading}>
                    <LoadingSpinner />
                </div>
            )}
            <div className={styles.cardTopBar}>
                <div className={styles.headerRow}>
                    <div
                        className={styles.createdBy}
                        title={`Created by ${ticket?.createdBy}`}
                    >
                        {ticket?.createdBy === 'Auto' ? (
                            <SettingsSuggest fontSize="small" />
                        ) : ticket.createdBy === 'Confirmed Auto' ? (
                            <Verified fontSize="small" />
                        ) : (
                            <Person fontSize="small" />
                        )}
                    </div>
                    <div
                        className={styles.statusPill}
                        style={{
                            backgroundColor:
                                convertDeliveryStatusToStatusColor[
                                    ticket?.status
                                ],
                        }}
                    >
                        {convertDeliveryStatusToDisplayName[ticket?.status]}
                    </div>
                </div>

                <Checkbox
                    checked={selectedTickets.includes(ticket.id)}
                    size="small"
                    onChange={(e) => {
                        if (e.target.checked) {
                            setSelectedTickets([...selectedTickets, ticket.id]);
                        } else {
                            setSelectedTickets(
                                selectedTickets.filter((t) => t !== ticket.id)
                            );
                        }
                    }}
                    sx={{ paddingRight: 0 }}
                />
            </div>
            <div className={styles.deviceName}>
                <Button
                    onClick={() => setActiveTicket(ticket)}
                    variant="text"
                    title={
                        ticket?.name ?? device?.name ?? device?.shortDeviceId
                    }
                >
                    <span>
                        {ticket?.name ?? device?.name ?? device?.shortDeviceId}
                    </span>
                </Button>
                {device?.activeIssueTicket && (
                    <IconButton
                        onClick={(e) => {
                            e.stopPropagation();

                            navigate(
                                `/device-details/${device?.deviceId}/issues`,
                                {
                                    state: device,
                                }
                            );
                        }}
                    >
                        <IssueCauseIcon
                            trigger={device.activeIssueTicket?.ticketTrigger}
                            fontSize="small"
                            color="warning"
                        />
                    </IconButton>
                )}
            </div>
            <Typography
                variant="body2"
                noWrap
                textOverflow="ellipsis"
                title={device?.customer_name}
            >
                {device?.customer_name}
            </Typography>
            <div>
                {missingInfo && (
                    <Tooltip
                        className={styles.ticketWarning}
                        title="This ticket is missing delivery information."
                    >
                        <IconButton onClick={() => setActiveTicket(ticket)}>
                            <Warning fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </div>
            <div className={styles.ticketTotal}>
                {ticket.status === 'done' ? 'Delivered' : 'Planned'}:{' '}
                {ticket.status === 'done'
                    ? Math.round(
                          Number(ticket?.gallonsDelivered || 0)
                      ).toLocaleString()
                    : Math.round(
                          Number(
                              ticket?.plannedGallons ||
                                  maxValidFill(devicesList[ticket.deviceId]) ||
                                  0
                          )
                      ).toLocaleString()}{' '}
                gal
            </div>
        </Card>
    );
};
